import { z } from 'zod';
import { isMonthDay, isPlainDate } from './dates.js';
import { ISSUE_PRIORITY, ISSUE_STATUS, OBJECT_KIND, OBJECT_KIND_IN_USE, PHOTO_REQUIREMENT, TASK_STATUS, TASK_HINTS, ABSENCE_KINDS, OCCURRENCE_GROUPS, OCCURRENCE_SEARCH_SCOPES, OCCURRENCE_SORTS, } from './constants.js';
export const plainDate = z.string().refine(isPlainDate, 'Ungültiges Datum (erwartet JJJJ-MM-TT)');
export const monthDay = z.string().refine(isMonthDay, 'Ungültiger Saisonwert (erwartet MM-TT)');
const weekday = z.number().int().min(0).max(6);
const weekdays = z.array(weekday).min(1, 'Mindestens ein Wochentag').max(7);
const trimmed = (max) => z.string().trim().min(1, 'Pflichtfeld').max(max);
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));
export const recurrenceSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('weekly'), weekdays }),
    z.object({
        type: z.literal('interval_weeks'),
        weekdays,
        intervalWeeks: z.number().int().min(1).max(12),
        anchorDate: plainDate,
    }),
    z.object({
        type: z.literal('monthly_nth_weekday'),
        nth: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(-1)]),
        weekday,
    }),
    z.object({ type: z.literal('monthly_day'), day: z.number().int().min(1).max(31) }),
    z.object({ type: z.literal('once'), date: plainDate }),
]);
// ---------------------------------------------------------------- auth
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email('Bitte eine gültige E-Mail-Adresse angeben'),
    password: z.string().min(1, 'Bitte Passwort eingeben'),
});
export const redeemInviteSchema = z.object({
    token: z.string().min(10),
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Der Code besteht aus 6 Ziffern'),
});
// ---------------------------------------------------------------- Stammdaten
/**
 * One node of the object tree, whatever its level. Address fields are meant for
 * Liegenschaften but allowed everywhere: a Gebäude on a large site can have its
 * own house number.
 */
export const objectInputSchema = z.object({
    kind: z.enum(OBJECT_KIND),
    parentId: z.string().uuid().nullish(),
    name: trimmed(160),
    /** Own number (Objektnummer, Wohnungsnummer), free text, searchable. */
    number: optionalText(60),
    /** ID in an external system, today Youtilly. */
    externalRef: optionalText(80),
    street: optionalText(160),
    zip: optionalText(16),
    city: optionalText(120),
    /** Contact for customers: name, phone, e-mail as free text. */
    contact: optionalText(400),
    notes: optionalText(2000),
    /** How to get in. Shown to the worker on every task here and below. */
    accessNote: optionalText(500),
    /** Wirtschaftseinheit as the customer counts it, e.g. "WE 4711". */
    economicUnit: optionalText(80),
    /** Further master data as label and value: Baujahr, Fläche, Anzahl Wohnungen. */
    attributes: z.array(z.object({ label: trimmed(60), value: z.string().trim().max(300) })).max(30).optional(),
    sortOrder: z.number().int().min(0).max(99999).optional(),
});
/** Kind is fixed after creation; moving a node (new parent) is allowed. */
export const objectPatchSchema = objectInputSchema.omit({ kind: true }).partial();
export const objectQuerySchema = z.object({
    parentId: z.string().uuid().optional(),
    /** `root` lists the customers. */
    root: z.enum(['true', 'false']).optional(),
    kind: z.enum(OBJECT_KIND).optional(),
    q: z.string().trim().max(120).optional(),
    includeArchived: z.enum(['true', 'false']).optional(),
    limit: z.coerce.number().int().min(1).max(500).optional(),
});
export const staffInputSchema = z.object({
    name: trimmed(160),
    email: z.string().trim().toLowerCase().email().optional().or(z.literal('').transform(() => undefined)),
    phone: optionalText(40),
    locale: z.string().trim().max(8).optional(),
    /** Browser access for people who plan a tour of dozens of addresses a day. */
    planningAccess: z.boolean().optional(),
});
/** A group of Minijobber sharing tasks; whoever completes first did the work. */
export const teamInputSchema = z.object({
    name: trimmed(120),
    note: optionalText(500),
    memberIds: z.array(z.string().uuid()).max(200).default([]),
});
export const templateInputSchema = z.object({
    name: trimmed(160),
    description: optionalText(2000),
    photoRequirement: z.enum(PHOTO_REQUIREMENT).default('after'),
    estimatedMinutes: z.number().int().min(0).max(600).optional(),
    /**
     * Fixed wording the worker must pick from when completing, e.g. for a
     * Legionellen check "Begehung und Spülung" vs. "nur Begehung". Non-empty
     * means mandatory, because the choice decides what gets billed.
     */
    textOptions: z.array(trimmed(120)).max(30).default([]),
    /** The options that need a short reason, e.g. "kein Zugang": the office calls back. */
    reasonOptions: z.array(trimmed(120)).max(30).default([]),
    /**
     * Options that need no photo although the Leistung asks for one, e.g.
     * "kein Zugang" on a Legionellen check: there is nothing to photograph.
     */
    photoFreeOptions: z.array(trimmed(120)).max(30).default([]),
    /** Options after which the office calls back, marked in the lists ("kein Zugang"). */
    callbackOptions: z.array(trimmed(120)).max(30).default([]),
    /** Part of the Legionellen area: month lists, Mangelmeldung, Monatsreport. */
    legionella: z.boolean().default(false),
    /** A value to note on completion, e.g. Wassertemperatur in °C. */
    measurementLabel: optionalText(80).nullable(),
    measurementUnit: optionalText(20).nullable(),
    /** A signature on site: none, optional or required. */
    signature: z.enum(['none', 'optional', 'required']).default('none'),
});
// ---------------------------------------------------------------- Pläne
export const planFieldsSchema = z.object({
    objectId: z.string().uuid(),
    /** One task per object of this kind below `objectId`; null means the object itself. */
    spreadKind: z.enum(OBJECT_KIND_IN_USE).nullish(),
    templateId: z.string().uuid(),
    assigneeId: z.string().uuid().nullish(),
    /** A group instead of a person; the two are exclusive, the group wins. */
    assigneeTeamId: z.string().uuid().nullish(),
    recurrence: recurrenceSchema,
    startDate: plainDate,
    endDate: plainDate.nullish(),
    seasonStart: monthDay.nullish(),
    seasonEnd: monthDay.nullish(),
    dueTime: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Uhrzeit im Format HH:MM')
        .nullish(),
    active: z.boolean().default(true),
});
const withPlanRules = (schema) => schema
    .refine((v) => !v.endDate || !v.startDate || v.endDate >= v.startDate, {
    message: 'Das Enddatum darf nicht vor dem Startdatum liegen',
    path: ['endDate'],
})
    .refine((v) => (v.seasonStart == null) === (v.seasonEnd == null), {
    message: 'Saison braucht Anfang und Ende',
    path: ['seasonEnd'],
});
export const planInputSchema = withPlanRules(planFieldsSchema);
export const planPatchSchema = withPlanRules(planFieldsSchema.partial());
// ---------------------------------------------------------------- Aufgaben
export const adhocTaskSchema = z.object({
    objectId: z.string().uuid(),
    templateId: z.string().uuid().nullish(),
    title: optionalText(160),
    assigneeId: z.string().uuid().nullish(),
    assigneeTeamId: z.string().uuid().nullish(),
    /**
     * Sonderaufgabe in Menge: one task per object of this kind below
     * `objectId`, e.g. Winterdienst for every Liegenschaft of a customer.
     */
    spreadKind: z.enum(OBJECT_KIND_IN_USE).nullish(),
    dueDate: plainDate,
    note: optionalText(2000),
    photoRequirement: z.enum(PHOTO_REQUIREMENT).optional(),
    /** Youtilly task ID when the task comes from a customer order. */
    externalRef: optionalText(80),
});
export const occurrenceFilterSchema = z.object({
    from: plainDate.optional(),
    to: plainDate.optional(),
    /** Any node: filters the whole subtree below it. */
    objectId: z.string().uuid().optional(),
    assigneeId: z.string().uuid().optional(),
    teamId: z.string().uuid().optional(),
    status: z.enum(TASK_STATUS).optional(),
    /** Several statuses at once, comma separated, e.g. `open,missed`. */
    statuses: z.string().max(80).optional(),
    templateId: z.string().uuid().optional(),
    /** The tasks one plan produced, from the plan's "Aufgaben anzeigen". */
    planId: z.string().uuid().optional(),
    hint: z.enum(TASK_HINTS).optional(),
    /** Free text over Leistung, Objekt, Person, Notiz and IDs. */
    q: z.string().trim().max(100).optional(),
    /** Where `q` looks: one kind of column instead of all, so "Müll" does not find the Müllerstraße. */
    qIn: z.enum(OCCURRENCE_SEARCH_SCOPES).optional(),
    /** Column the task list is sorted by; without it, by date. */
    sort: z.enum(OCCURRENCE_SORTS).optional(),
    dir: z.enum(['asc', 'desc']).optional(),
    /** Paging for the task list; the calendar keeps the old single page. */
    limit: z.coerce.number().int().min(1).max(5000).optional(),
    offset: z.coerce.number().int().min(0).optional(),
    /** Grouped list: the grouping, and with `groupKey` one group's tasks (`-` for "ohne"). */
    groupBy: z.enum(OCCURRENCE_GROUPS).optional(),
    groupKey: z.string().max(200).optional(),
    /** Legionellen: the tasks of one month list (without a list, due in that month), dates ignored. */
    listMonth: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
    legionella: z.enum(['1']).optional(),
});
/** Krank or Urlaub for a period; inactive is the person's own switch. */
export const absenceInputSchema = z
    .object({
    kind: z.enum(ABSENCE_KINDS),
    from: plainDate,
    until: plainDate,
    note: optionalText(300),
})
    .refine((v) => v.until >= v.from, { message: 'Das Ende liegt vor dem Beginn', path: ['until'] });
/** Several people krank or im Urlaub at once, e.g. a team down with flu. */
export const absenceBulkSchema = z
    .object({
    ids: z.array(z.string().uuid()).min(1).max(500),
    kind: z.enum(ABSENCE_KINDS),
    from: plainDate,
    until: plainDate,
    note: optionalText(300),
})
    .refine((v) => v.until >= v.from, { message: 'Das Ende liegt vor dem Beginn', path: ['until'] });
/** One change for many plans at once, e.g. every Tuesday becomes Thursday. */
export const planBulkSchema = z
    .object({
    ids: z.array(z.string().uuid()).min(1).max(1000),
    action: z.enum(['assign', 'weekday', 'recurrence', 'deactivate']),
    assigneeId: z.string().uuid().nullish(),
    assigneeTeamId: z.string().uuid().nullish(),
    /** 0 Sunday to 6 Saturday, as in the recurrence rules. */
    fromWeekday: z.number().int().min(0).max(6).nullish(),
    toWeekday: z.number().int().min(0).max(6).optional(),
    /** One new rhythm for all chosen plans. */
    recurrence: recurrenceSchema.optional(),
})
    .refine((v) => v.action !== 'weekday' || v.toWeekday !== undefined, { message: 'Bitte den neuen Wochentag wählen', path: ['toWeekday'] })
    .refine((v) => v.action !== 'recurrence' || v.recurrence !== undefined, { message: 'Bitte den neuen Rhythmus wählen', path: ['recurrence'] });
export const occurrencePatchSchema = z.object({
    assigneeId: z.string().uuid().nullish(),
    assigneeTeamId: z.string().uuid().nullish(),
    dueDate: plainDate.optional(),
    status: z.enum(['open', 'skipped']).optional(),
    note: optionalText(2000),
    externalRef: optionalText(80).nullable(),
});
/**
 * Mehrfachauswahl: several tasks at once. Only unfinished tasks change;
 * done and closed ones are part of the proof and stay as they are.
 */
export const bulkTaskSchema = z
    .object({
    ids: z.array(z.string().uuid()).min(1).max(5000),
    action: z.enum(['assign', 'move', 'skip', 'done', 'final_missed']),
    assigneeId: z.string().uuid().nullish(),
    assigneeTeamId: z.string().uuid().nullish(),
    dueDate: plainDate.optional(),
    /** Why the office records `done` (required) or closes for good (optional). */
    reason: optionalText(500),
})
    .refine((v) => v.action !== 'move' || Boolean(v.dueDate), { message: 'Bitte ein Datum wählen', path: ['dueDate'] })
    .refine((v) => v.action !== 'done' || Boolean(v.reason), { message: 'Bitte angeben, woher die Rückmeldung kommt', path: ['reason'] });
/** The Verwaltung closes a task for good: it stays in the record, never in a list. */
export const finalMissedSchema = z.object({
    reason: optionalText(500),
});
/**
 * A completion posted by the app. `clientRef` makes the call idempotent so the
 * offline outbox can retry without creating duplicates, and `completedAtClient`
 * preserves when the work actually happened even if the upload lands days later.
 */
export const completionSchema = z.object({
    clientRef: z.string().uuid(),
    completedAtClient: z.string().datetime({ offset: true }),
    note: optionalText(2000),
    /** One of the template's `textOptions`; required when the template has any. */
    textOption: optionalText(120),
    photoIds: z.array(z.string().uuid()).max(31).default([]),
    /** Required when the Leistung asks for a Messwert. */
    measurementValue: z.number().finite().min(-100000).max(1000000).nullish(),
    /** Name of whoever signed on site; the image is a photo of kind `signature`. */
    signedBy: optionalText(120),
    /** Mangelmeldung, e.g. Taubennest or offene Elektrik; the office follows up. */
    defectNote: optionalText(1000),
    /** Only sent when GPS is on and the person agreed in the app. */
    location: z
        .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180), accuracy: z.number().min(0).max(100000) })
        .nullish(),
});
export const backofficeCompletionSchema = z.object({
    completedAtClient: z.string().datetime({ offset: true }).optional(),
    measurementValue: z.number().finite().nullish(),
    performedById: z.string().uuid().nullish(),
    note: optionalText(2000),
    textOption: optionalText(120),
    defectNote: optionalText(1000),
    reason: trimmed(500),
});
/**
 * The monthly Legionellen list (Leerwohnungen): one task per row, on the
 * Gebäude found by its address, the flat as text on the task.
 */
export const legionellaImportSchema = z.object({
    csv: z.string().min(1).max(5_000_000),
    mapping: z.record(z.string(), z.number().int().min(0)).optional(),
    templateId: z.string().uuid(),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Monat als JJJJ-MM'),
    assigneeId: z.string().uuid().optional(),
    /** The customer the list comes from; houses that are no objects hang there. */
    customerId: z.string().uuid(),
    dryRun: z.boolean().default(true),
});
/** "Rückruf geklärt", optionally with a new visit for the same flat. */
export const callbackResolveSchema = z.object({
    note: z.string().trim().max(1000).optional(),
    followUpDate: plainDate.optional(),
    /** Undo: the call back is open again. */
    reopen: z.boolean().optional(),
});
// ---------------------------------------------------------------- Mängel
export const issueInputSchema = z.object({
    /** Makes the offline outbox safe to retry, see `completionSchema`. */
    clientRef: z.string().uuid().optional(),
    objectId: z.string().uuid(),
    occurrenceId: z.string().uuid().nullish(),
    title: trimmed(200),
    description: optionalText(4000),
    priority: z.enum(ISSUE_PRIORITY).default('normal'),
    photoIds: z.array(z.string().uuid()).max(10).default([]),
});
export const issuePatchSchema = z.object({
    status: z.enum(ISSUE_STATUS).optional(),
    priority: z.enum(ISSUE_PRIORITY).optional(),
    resolutionNote: optionalText(4000),
});
// ---------------------------------------------------------------- Nachweis
export const reportQuerySchema = z
    .object({
    /** A Liegenschaft normally; any node works and covers its subtree. */
    objectId: z.string().uuid(),
    from: plainDate,
    to: plainDate,
    includePhotos: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(true)
        .transform((v) => v === true || v === 'true'),
})
    .refine((v) => v.to >= v.from, { message: 'Der Zeitraum ist ungültig', path: ['to'] });
export const reportLinkSchema = z
    .object({
    objectId: z.string().uuid(),
    label: optionalText(160),
    /** Either a fixed window, or a window that keeps moving with today. */
    mode: z.enum(['rolling', 'fixed']).default('rolling'),
    rollingDays: z.number().int().min(7).max(400).default(90),
    from: plainDate.optional(),
    to: plainDate.optional(),
    includePhotos: z.boolean().default(true),
    showStaffNames: z.boolean().default(true),
    expiresInDays: z.number().int().min(1).max(400).default(90),
})
    .refine((v) => v.mode !== 'fixed' || (v.from && v.to && v.to >= v.from), {
    message: 'Für einen festen Zeitraum werden Anfang und Ende gebraucht',
    path: ['to'],
});
/** Sperrfenster: no tasks here and below between the two dates. */
export const blockSchema = z
    .object({
    from: plainDate,
    until: plainDate,
    reason: trimmed(200),
})
    .refine((v) => v.until >= v.from, { message: 'Das Ende liegt vor dem Anfang', path: ['until'] });
/** Several tasks to new people in one go: Vertretung, Umverteilung. Null unassigns. */
export const assignSchema = z.object({
    assignments: z
        .array(z.object({ id: z.string().uuid(), assigneeId: z.string().uuid().nullable() }))
        .min(1)
        .max(2000),
});
/**
 * Vertretung for a period in one step: tasks that exist move now, and the
 * rules make the scheduler give later tasks to the same people. A rule
 * without `siteId` covers every Liegenschaft not named in another rule.
 */
export const coverSchema = z
    .object({
    from: plainDate,
    to: plainDate,
    assignments: z.array(z.object({ id: z.string().uuid(), assigneeId: z.string().uuid().nullable() })).max(2000),
    rules: z.array(z.object({ siteId: z.string().uuid().nullable(), substituteId: z.string().uuid() })).max(500),
})
    .refine((v) => v.to >= v.from, { message: 'Das Ende liegt vor dem Beginn', path: ['to'] })
    .refine((v) => v.assignments.length > 0 || v.rules.length > 0, { message: 'Bitte mindestens eine Vertretung wählen', path: ['rules'] });
export const qualityReviewSchema = z.object({
    status: z.enum(['bestaetigt', 'verworfen']),
});
export const settingsPatchSchema = z.object({
    ai: z
        .object({
        provider: z.enum(['off', 'anthropic', 'bedrock', 'local']).optional(),
        photoCheck: z.boolean().optional(),
        importAssist: z.boolean().optional(),
    })
        .optional(),
    localPhotoCheck: z.boolean().optional(),
    /** Qualitätsbegehung: score from which an inspection counts as passed. */
    qualityThreshold: z.number().int().min(50).max(100).optional(),
    /** Standort bei Erledigung; each person still decides in the app. */
    gps: z.object({ enabled: z.boolean() }).optional(),
});
export const localeSchema = z.object({
    locale: z.enum(['de', 'tr', 'pl', 'uk', 'ar']),
});
// ---------------------------------------------------------------- Import und Übergabe
/** Field name to column index, chosen by hand or suggested. Overrides the header detection. */
const columnMapping = z.record(z.string(), z.number().int().min(0).max(200)).optional();
export const mappingRequestSchema = z.object({
    csv: z.string().min(1),
    target: z.enum(['objects', 'tasks']),
    /** Ask the AI for a suggestion; needs import assistance switched on. */
    useAi: z.boolean().default(false),
});
/**
 * A task list from outside, the monthly Legionellen sheet, for instance. Rows
 * are matched to objects by Youtilly ID first, then by address and unit number.
 */
export const taskImportSchema = z.object({
    csv: z.string().min(1),
    mapping: columnMapping,
    /** Applied to every row without its own Leistung column. */
    templateId: z.string().uuid().nullish(),
    assigneeId: z.string().uuid().nullish(),
    /** Preview only: nothing is written. */
    dryRun: z.boolean().default(true),
});
export const objectImportSchema = z.object({
    csv: z.string().min(1),
    mapping: columnMapping,
    dryRun: z.boolean().default(true),
});
export const handoverQuerySchema = z.object({
    from: plainDate,
    to: plainDate,
    /** `true` stamps the rows as exported so the next run leaves them out. */
    markExported: z.enum(['true', 'false']).optional(),
    includeExported: z.enum(['true', 'false']).optional(),
});
// ---------------------------------------------------------------- Auswertungen
export const EXPORT_DATASETS = ['tasks', 'issues'];
const csvList = z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => (Array.isArray(v) ? v : v ? v.split(',') : []).map((x) => x.trim()).filter(Boolean));
/**
 * One export: which list, which columns in which order, which rows. Sent as a
 * query string so a plain link can download the file.
 */
export const exportQuerySchema = z
    .object({
    dataset: z.enum(EXPORT_DATASETS).default('tasks'),
    columns: csvList,
    from: plainDate,
    to: plainDate,
    /** Any node: the whole subtree below it. */
    objectId: z.string().uuid().optional(),
    status: csvList,
    templateId: z.string().uuid().optional(),
    /** Assigned to or done by this person. */
    personId: z.string().uuid().optional(),
    teamId: z.string().uuid().optional(),
    /** Only tasks with a Youtilly ID. */
    withExternalRef: z.enum(['true', 'false']).optional(),
    format: z.enum(['xlsx', 'csv']).default('xlsx'),
    /** File and sheet name, e.g. the preset's name. */
    title: optionalText(80),
})
    .refine((v) => v.to >= v.from, { message: 'Der Zeitraum ist ungültig', path: ['to'] });
export const exportProfileSchema = z.object({
    name: trimmed(80),
    dataset: z.enum(EXPORT_DATASETS),
    columns: z.array(z.string().max(40)).min(1).max(60),
    status: z.array(z.string().max(20)).max(10).default([]),
    withExternalRef: z.boolean().default(false),
});
// ---------------------------------------------------------------- Kundenportal
export const TICKET_CATEGORIES = ['besichtigung', 'haushaltshilfe', 'reparatur', 'sonstiges'];
export const TICKET_CATEGORY_LABEL = {
    besichtigung: 'Wohnungsbesichtigung',
    haushaltshilfe: 'Unterstützung im Haushalt',
    reparatur: 'Reparatur oder Mangel',
    sonstiges: 'Sonstiges',
};
/** A login for a customer: sees only that customer's Liegenschaften, optionally fewer. */
export const customerUserSchema = z.object({
    name: trimmed(160),
    email: z.string().trim().toLowerCase().email('Bitte eine gültige E-Mail-Adresse'),
    /** Limit to these Liegenschaften; empty or missing means all of the customer's. */
    siteIds: z.array(z.string().uuid()).max(500).nullish(),
});
export const customerUserPatchSchema = z.object({
    name: trimmed(160).optional(),
    siteIds: z.array(z.string().uuid()).max(500).nullish(),
    active: z.boolean().optional(),
});
export const password = z.string().min(10, 'Mindestens 10 Zeichen').max(200);
export const portalInviteRedeemSchema = z.object({
    token: z.string().min(20).max(200),
    password,
});
export const passwordChangeSchema = z.object({
    current: z.string().min(1).max(200),
    next: password,
});
/** A customer's request through the portal. */
export const ticketInputSchema = z.object({
    /** A Liegenschaft or anything below it that the customer may see. */
    objectId: z.string().uuid(),
    category: z.enum(TICKET_CATEGORIES),
    title: trimmed(160),
    description: optionalText(4000),
    requestedDate: plainDate.optional(),
    /** Who to meet on site: prospective tenant, tenant, social worker. */
    contact: optionalText(300),
    photoIds: z.array(z.string().uuid()).max(5).default([]),
});
export const ticketMessageSchema = z.object({
    body: trimmed(4000),
    /** Verwaltung only: a note the customer never sees. */
    internal: z.boolean().default(false),
    /** Verwaltung only: `waiting` asks the customer something and waits for the answer. */
    status: z.enum(['open', 'in_progress', 'waiting', 'done', 'rejected']).optional(),
});
/** Turns a ticket into a task, e.g. the viewing on the wished date. */
export const ticketToTaskSchema = z.object({
    dueDate: plainDate,
    title: optionalText(160),
    templateId: z.string().uuid().nullish(),
    assigneeId: z.string().uuid().nullish(),
    assigneeTeamId: z.string().uuid().nullish(),
    note: optionalText(2000),
});
// ---------------------------------------------------------------- Qualitätsbegehung
export const INSPECTION_RATINGS = ['ok', 'maengel', 'nicht_ok', 'na'];
export const checklistSchema = z.object({
    name: trimmed(120),
    templateId: z.string().uuid().nullish(),
    items: z
        .array(z.object({
        id: z.string().max(40).optional(),
        label: trimmed(160),
        weight: z.number().int().min(1).max(3).default(1),
    }))
        .min(1, 'Mindestens ein Prüfpunkt')
        .max(60),
});
export const inspectionSchema = z.object({
    objectId: z.string().uuid(),
    checklistId: z.string().uuid(),
    inspectedAt: z.string().datetime({ offset: true }).optional(),
    results: z
        .array(z.object({
        itemId: z.string().max(40),
        rating: z.enum(INSPECTION_RATINGS),
        note: optionalText(500),
        /** Create a Mangel from this line, for what is not in order. */
        createIssue: z.boolean().default(false),
    }))
        .min(1),
    note: optionalText(2000),
    photoIds: z.array(z.string().uuid()).max(10).default([]),
    fromSample: z.boolean().default(false),
});
/**
 * Weighted score in percent: in Ordnung counts fully, kleine Mängel half,
 * nicht in Ordnung not at all; "entfällt" leaves the line out.
 */
export function inspectionScore(lines) {
    const counted = lines.filter((l) => l.rating !== 'na');
    const total = counted.reduce((sum, l) => sum + l.weight, 0);
    if (total === 0)
        return 100;
    const got = counted.reduce((sum, l) => sum + l.weight * (l.rating === 'ok' ? 1 : l.rating === 'maengel' ? 0.5 : 0), 0);
    return Math.round((got / total) * 100);
}
export const locationConsentSchema = z.object({ shareLocation: z.boolean() });
/** A login for the office: Verwaltung or Administrator. */
export const officeUserSchema = z.object({
    name: trimmed(160),
    email: z.string().trim().toLowerCase().email('Bitte eine gültige E-Mail-Adresse'),
    role: z.enum(['verwalter', 'admin']).default('verwalter'),
});
export const officeUserPatchSchema = z.object({
    name: trimmed(160).optional(),
    role: z.enum(['verwalter', 'admin']).optional(),
    active: z.boolean().optional(),
});
//# sourceMappingURL=schemas.js.map